<template>
  <div class="container is-fluid py-5">

    <div class="columns is-multiline">
      
      <div class="column is-full">
        <div class="level">
          <div class="level-left buttons mb-0">
            <button 
              class="button is-link mb-0" 
              :class="{ 'is-loading': saving }"
              :disabled="!valid || saving"
              @click.stop="save()">
              <span class="icon is-medium">
                <i class="fas fa-save"></i>
              </span>
              <span>              
                Save Production Sheet
              </span>
            </button>

            <button class="button is-primary mb-0" @click="addRow()">
              Add a Slab
            </button>

            <button class="button mb-0" 
              v-if="selected.length > 0"
              @click="deleteSelected()">
              <span class="icon is-medium">
                <i class="fas fa-trash has-text-danger"></i>
              </span>
              <span>
                Delete {{ selected.length }} Item<span v-if="selected.length > 0">s</span>
              </span>
            </button>
          </div>
        </div>
      </div>

      <v-form
        class="column is-full columns" 
        :valid.sync="valid">

        <div class="column is-3">
          <label class="label">Project*</label>
          <search-autocomplete
            v-model="productionSheet.project_id">
          </search-autocomplete>
        </div>

        <div class="column is-3">
          <label class="label">Production Sheet Name*</label>
          <v-text-field
            required
            placeholder="Production Sheet Name"
            v-model="productionSheet.name">
          </v-text-field>
        </div>
        
        <div class="column is-3">
          <label class="label">Description</label>
          <v-text-field
            placeholder="Production Sheet Description"
            rows="3"
            v-model="productionSheet.desc">
          </v-text-field>
        </div>

        <div class="column is-3">
          <label class="label">Drawing Upload</label>
          <v-file-upload
            has-name
            v-model="image">
          </v-file-upload>
        </div>

      </v-form>

      <div class="column is-full py-0">
        <hr class="my-2"/>
      </div>

      <div class="column is-full">
        

        <table class="table is-hoverable is-fullwidth has-small-font">
          <thead>
            <tr>
              <th rowspan="2">
                <v-checkbox
                  :value="allSelected"
                  @input="toggleAllSelected()">
                </v-checkbox>
              </th>
              <th rowspan="2">
                Backing Product
              </th>
              <th rowspan="2">
                Backing Thickness"
              </th>
              <th rowspan="2">
                Facing Product
              </th>
              <th rowspan="2">
                Facing Thickness"
              </th>
              <th rowspan="2">
                Finish
              </th>
              <th colspan="3" class="has-text-centered fixed-width--large">
                Slab Dimensions
              </th>
              <th rowspan="2">
                Finished Quantity
              </th>
              <th rowspan="2">
                Location
              </th>
              <th rowspan="2">
                Desc
              </th>
              <th rowspan="2"></th>
            </tr>
            <tr class="has-text-centered">
              <th>L"</th><th>W"</th><th>T"</th>
            </tr>
          </thead>
          <tbody>
            <tr 
              v-for="(item, i) in productionSheetItems"
              :key="i">
              <td>
                <v-checkbox
                  class="mt-2"
                  :value="selected.indexOf(item.id) != -1"
                  @input="toggleSelected(item.id)">
                </v-checkbox>
              </td>
              <td class="fixed-width">
                <product-selector 
                  :product-category-id="14"
                  disable-category
                  v-model="item.b_product_master_id"/>
              </td>
              <td>
                <v-content-editable
                  required
                  class="text--nowrap"
                  v-model="item.b_thick_in_str"/>
              </td>
              <td class="fixed-width">
                <product-selector 
                  :product-category-id="13"
                  disable-category
                  has-product-search
                  v-model="item.f_product_master_id"
                />
              </td>
              <td>
                <v-content-editable
                  required
                  class="text--nowrap"
                  v-model="item.f_thick_in_str"
                />
              </td>
              <td class="fixed-width">
                <v-select 
                  :options="processingMethods.filter(d => d.processing_type_id == 2)"
                  item-text="processing_name"
                  item-value="id"
                  v-model="item.finishing_step_id"
                />
              </td>
              <td>
                <v-content-editable
                  required
                  class="text--nowrap"
                  min-width="30px"
                  v-model="item.length_in_str"
                />
              </td>
              <td>
                <v-content-editable
                  required
                  class="text--nowrap"
                  min-width="30px"
                  v-model="item.width_in_str"
                />
              </td>
              <td>
                <v-content-editable
                  required
                  class="text--nowrap"
                  min-width="30px"
                  v-model="item.s_thick_in_str"/>
              </td>
              <td>
                <v-text-field 
                  required
                  placeholder="Finished Qty"
                  v-model="item.finished_qty"/>
              </td>
              <td>
                <v-text-field 
                  placeholder="Item Location"
                  v-model="item.location"/>
              </td>
              <td>
                <v-text-field
                  placeholder="Item Description"
                  v-model="item.desc"/>
              </td>
              <td>
                <div class="buttons buttons-right mt-1">
                  <button  
                    @click="removeRow(item.id)"
                    class="button is-small">
                    <span class="has-text-grey icon">
                      <i class="fas fa-trash"></i>
                    </span>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>    

    </div>

  </div>
</template>

<script>
import VForm from '@/components/base/VForm'
import VTextField from '@/components/base/VTextField'
import VContentEditable from '@/components/base/VContentEditable'
import VCheckbox from '@/components/base/VCheckbox'
import VFileUpload from '@/components/base/VFileUpload'
import VSelect from '@/components/base/VSelect'
import SearchAutocomplete from '@/components/projects/SearchAutocomplete'
import ProductSelector from '@/components/inventory/ProductSelector'

import { mapState, mapActions, mapMutations } from 'vuex'

export default {
  components: { 
    VTextField,
    VContentEditable,
    VForm,
    VCheckbox,
    VFileUpload,
    VSelect,
    SearchAutocomplete,
    ProductSelector
  },
  data() {
    return {
      defaultRows: 1,
      productionSheet: {
        project_id: null,
        desc: null
      },
      productionSheetItems: [],
      image: {
        name: null,
        file: null
      },
      selected: [],
      allSelected: false,
      headers: [
        { label: 'Backing Product', field: 'b_product_master_id', default: null },
        { label: 'Backing T"', field: 'b_thick_in_str', default: null },
        { label: 'Facing Product', field: 'f_product_master_id', default: null },
        { label: 'Facing T"', field: 'f_thick_in_str', default: null },
        { label: 'Facing Finish', field: 'finishing_step_id', default: null },
        { label: 'L"', field: 'length_in_str', default: null },
        { label: 'W"', field: 'width_in_str', default: null },
        { label: 'T"', field: 's_thick_in_str', default: null },
        { label: 'Finished Quantity', field: 'finished_qty', default: null },
        { label: 'Location', field: 'location', default: null },
        { label: 'Description', field: 'desc', default: null },
      ],
      valid: false,
      saving: false
    }
  },
  created() {
    this.initialize();
  },
  computed: {
    ...mapState("processings", ["processingMethods"]),
    template() {
      let obj = {};
      this.headers.forEach(col => {
        obj[col.field] = col.default;
      });

      return obj
    },
    ids() {
      return this.productionSheetItems.map(d => d.id);
    },
    totFinishedQty() {
      return this.productionSheetItems
        .map(d => d.f_finished_qty)
        .reduce((a,b) => a + parseInt(b) || 0, 0);
    },
    totOrderQty() {
      return this.productionSheetItems
        .map(d => d.f_qty_to_be_ordered)
        .reduce((a,b) => a + parseInt(b) || 0, 0);
    }
  },
  watch: {
    totFinishedQty(v) {
      this.productionSheet.b_finished_qty = v;
    },
    totOrderQty(v) {
      this.productionSheet.b_qty_to_be_ordered = v;
    }
  },
  methods: {
    ...mapActions("productionSheets", [
      "addProductionSheet", "addItem", "uploadImage"
    ]),
    ...mapActions("processings", ["getProcessingMethods"]),
    ...mapMutations("app", ["setNotification"]),
    initialize() {
      if (this.processingMethods.length == 0) {
        this.getProcessingMethods({ per_page: 100, processing_type_id: 2 });
      }

      for (let i=1; i <= this.defaultRows; i++) {
        this.addRow();
      }
    },
    addRow() {
      let newRow = Object.assign({}, this.template);
      newRow.id = this.ids.length == 0 ? 1 : Math.max(...this.ids) + 1;

      this.productionSheetItems.push(newRow);
    },
    removeRow(id) {
      this.productionSheetItems.forEach((detail, i) => {
        if (detail.id == id) {
          this.productionSheetItems.splice(i, 1);
        }
      })
    },
    deleteSelected() {
      this.selected.forEach((id, i) => {
        this.productionSheetItems.forEach(detail => {
          if (detail.id == id) {
            this.productionSheetItems.splice(i, 1);
          }
        });
      });
      this.selected.splice(0, this.selected.length);
    },
    toggleSelected(id) {
      const index = this.selected.indexOf(id);
      if (index == -1) {
        this.selected.push(id);
      } else {
        this.selected.splice(index, 1);
        if (this.selected.length == 0) {
          this.allSelected = false;
        }
      }
    },
    toggleAllSelected(v) {
      this.allSelected = v;
      const { selected } = this;

      if (v) {
        this.ids.forEach(id => {
          if (selected.indexOf(id) == -1) {
            selected.push(id);
          }
        })
      } else {
        selected.splice(0, selected.length);
      }
    },
    save() {
      this.saving = true;

      this.addProductionSheet({ data: this.productionSheet })
        .then(resp => {
          let promises = [];

          let completedItems = this.productionSheetItems.filter(
            d => Object.values(d).reduce((a, b) => { 
              if (b == null) { a += 1 } 
            }, 0) != Object.values.length
          )

          completedItems.forEach((item, i) => {
            item.production_sheet_id = resp.data.id;
            promises.push(this.addItem(item));
          })

          if (this.image.file) {
            promises.push(this.saveImage(resp.data.id));
          }

          Promise.all(promises).then(() => {
            this.notifySuccess()
          }).catch(() => {
            this.saving = false;
          })
        })
    },
    saveImage(productionSheetId) {
      let formData = new FormData();
      formData.append('image', this.image.file, this.image.file.name);

      this.uploadImage({ 
        productionSheetId: productionSheetId, 
        data: formData
      });
    },
    notifySuccess() {
      this.saving = false;
      this.setNotification({
        type: 'success',
        status: true,
        message: 'Production sheet successfully added'
      });
      this.$router.push({ name: 'ProductionSheets' });
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/table.scss';
@import '@/styles/flexbox.scss';

.fixed-width {
  min-width: 150px;
  &--large {
    min-width: 300px;
  }
}
</style>